// Operations -> DISA Roster Changes (George 2026-11-01). DISA's "Roster Change - <NAME>"
// notices from the Outlook OccMed folder, mirrored by the MinisForum OccMed saver: an
// employee removed from one of our DISA policies, almost always because another company
// verified them onto its roster. A DISA certificate collected after the change means we
// tested them again, so they are back on our roster (George 2026-11-01, Chance Cantu).
// Someone still off our roster who is on (or scheduled for) an SSS job is flagged.
// Admin and leadership only, server-enforced.

import { useEffect, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtDate, fmtDateTime, chipBtn } from './UI';
import { CurrentJob } from './DisaRandomsTab';

const fmtEffective = (s) => {
  if (!s) return '';
  const [d, t] = s.split('T');
  const [y, mo, da] = d.split('-').map(Number);
  const [h, m] = (t || '').split(':').map(Number);
  const day = new Date(y, mo - 1, da).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return Number.isNaN(h) ? day : `${day} ${(h % 12) || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

const STATUS_COLORS = {
  'Back on our roster': [colors.greenSoft, '#065F46'],
  'Tested since: confirm': [colors.amberSoft, '#92400E'],
  'Off our roster': [colors.graySoft, colors.charcoal],
};

export default function DisaRosterChangesTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(false);
  const [onJobOnly, setOnJobOnly] = useState(false);

  const load = () => api.disaRosterChanges().then(d => { setData(d); setErr(''); }).catch(e => setErr(e.message));
  useEffect(() => { load(); }, []);

  const act = async (fn, ok) => {
    setBusy(true); setMsg('');
    try { const r = await fn(); if (ok) setMsg(ok(r)); await load(); }
    catch (e) { setMsg(`⚠ ${e.message}`); }
    finally { setBusy(false); }
  };
  const setNotEmployed = (c, on) => act(() => api.disaRosterChangeUpdate(c.id, { not_employed: on }),
    () => `${c.employee_name}: ${on ? 'marked Not employed on every DISA tab' : 'Not employed cleared'}`);
  const saveNotes = (c) => act(() => api.disaRosterChangeUpdate(c.id, { notes: notes[c.id] ?? c.notes ?? '' }), () => {
    setNotes(n => { const x = { ...n }; delete x[c.id]; return x; });
    return `${c.employee_name}: notes saved`;
  });
  const sync = () => act(() => api.disaRandomsSync(), r => r.ok
    ? (r.skipped ? 'Feed unchanged since the last load' : `Loaded ${r.roster_changes} roster changes`)
    : `⚠ ${r.error}`);

  if (err) return <div style={styles.errorBox}>{err}</div>;
  if (!data) return <div style={styles.card}>Loading DISA roster changes…</div>;
  const s = data.sync || {};
  const onJob = data.changes.filter(c => c.jobs.length && c.status !== 'Back on our roster');
  const back = data.changes.filter(c => c.status === 'Back on our roster').length;
  const rows = onJobOnly ? onJob : data.changes;

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0, color: colors.charcoal }}>🔁 DISA Roster Changes</h2>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4, maxWidth: 820 }}>
            Mirrored from DISA's Roster Change emails in the OccMed folder: an employee removed from one of our DISA
            policies, usually because another company verified them onto its roster. If that was done in error, DISAWorks
            &gt; Start a New Service &gt; Verify Membership moves them back.
            {' '}Last loaded {s.last_ok_at ? fmtDateTime(s.last_ok_at) : 'never'}
            {s.report_modified ? ` · feed written ${fmtDateTime(s.report_modified)}` : ''}.
            {s.last_status === 'error' && <span style={{ color: colors.red, fontWeight: 600 }}> Last sync failed: {s.last_error}</span>}
          </div>
        </div>
        <button style={styles.btnSecondary} disabled={busy} onClick={sync}>⟳ Sync now</button>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8, fontSize: 12 }}>
        <button onClick={() => setOnJobOnly(false)}
          style={chipBtn(colors.graySoft, colors.charcoal, !onJobOnly)}>
          All {data.changes.length}
        </button>
        {back > 0 && <span style={{ ...styles.pill, background: STATUS_COLORS['Back on our roster'][0], color: STATUS_COLORS['Back on our roster'][1] }}>Back on our roster {back}</span>}
        {onJob.length > 0 && (
          <button onClick={() => setOnJobOnly(!onJobOnly)} title="Off our DISA roster (no test since) but on or scheduled for an SSS job"
            style={chipBtn(colors.redSoft, '#991B1B', onJobOnly)}>
            Still on a job {onJob.length}
          </button>
        )}
      </div>
      {msg && <div style={{ fontSize: 13, margin: '6px 0', color: msg.startsWith('⚠') ? colors.red : '#065F46' }}>{msg}</div>}
      {!rows.length ? (
        <div style={{ color: colors.textMuted, padding: 12 }}>No DISA roster changes loaded yet. The OccMed saver runs hourly on the MinisForum.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['Effective', 'Employee', 'SSN', 'Change', 'Status', 'Job now', 'Notes'].map(h => <th key={h} style={styles.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.id} style={{ background: c.jobs.length && c.status !== 'Back on our roster' ? '#FEF2F2' : undefined }}>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{fmtEffective(c.effective_local)}</td>
                  <td style={styles.td}>
                    <div style={{ fontWeight: 600 }}>{c.employee_name}</div>
                    {c.roster_name
                      ? <div style={{ fontSize: 11, color: '#065F46' }}>Roster: {c.roster_name}</div>
                      : <div style={{ fontSize: 11, color: colors.textMuted }}>not matched to the Roster</div>}
                    {c.employee_phone && <div style={{ fontSize: 11 }}><a href={`tel:${c.employee_phone.replace(/[^\d+]/g, '')}`}>{c.employee_phone}</a></div>}
                    {c.employee_email && <div style={{ fontSize: 11, wordBreak: 'break-all' }}><a href={`mailto:${c.employee_email}`}>{c.employee_email}</a></div>}
                  </td>
                  <td style={styles.td}>{c.ssn_last4 ? `…${c.ssn_last4}` : ''}</td>
                  <td style={{ ...styles.td, fontSize: 12 }}>
                    {c.action && c.policy
                      ? <><b>{c.action[0].toUpperCase() + c.action.slice(1)}</b> {c.policy}</>
                      : (c.summary || '')}
                    {c.action && c.policy && c.summary && <div style={{ color: colors.textMuted }}>{c.summary.replace(/,?\s*this employee was .*$/i, '')}</div>}
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.pill, whiteSpace: 'nowrap', background: (STATUS_COLORS[c.status] || STATUS_COLORS['Off our roster'])[0], color: (STATUS_COLORS[c.status] || STATUS_COLORS['Off our roster'])[1] }}>{c.status}</span>
                    <div style={{ marginTop: 4 }}>
                      <select style={{ ...styles.input, padding: '3px 6px', width: 'auto', fontSize: 12 }} disabled={busy}
                        value={c.not_employed ? 'Not employed' : ''} title="Not employed applies to this person on every DISA tab (Randoms, Annual, Roster Changes), every month"
                        onChange={e => setNotEmployed(c, e.target.value === 'Not employed')}>
                        <option value="">Employed</option>
                        <option value="Not employed">Not employed</option>
                      </select>
                    </div>
                    {c.test_since && (
                      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 3 }}>
                        {c.test_since.source === 'DISA roster'
                          ? <>DISA roster test {fmtDate(c.test_since.on)}: no certificate here, may be another client's</>
                          : <>Tested {fmtDate(c.test_since.on)}{c.test_since.purpose ? ` · ${c.test_since.purpose}` : ''}{c.test_since.test_kind === 'alcohol' ? ' (alcohol)' : ''}{c.test_since.ccf_id ? ` · ${c.test_since.ccf_id}` : ''}{c.test_since.policy ? <div>{c.test_since.policy}</div> : null}</>}
                      </div>
                    )}
                  </td>
                  <td style={{ ...styles.td, minWidth: 150 }}>
                    {c.employee_id ? <CurrentJob jobs={c.jobs} /> : <span style={{ fontSize: 12, color: colors.textMuted }}>—</span>}
                  </td>
                  <td style={{ ...styles.td, minWidth: 180 }}>
                    <textarea style={{ ...styles.textarea, minHeight: 34, fontSize: 12 }} value={notes[c.id] ?? c.notes ?? ''}
                      onChange={e => setNotes(n => ({ ...n, [c.id]: e.target.value }))} />
                    {notes[c.id] !== undefined && (
                      <button style={{ ...styles.btnSecondary, padding: '2px 8px', fontSize: 12 }} disabled={busy} onClick={() => saveNotes(c)}>Save notes •</button>
                    )}
                    {c.updated_at && <div style={{ fontSize: 10, color: colors.textMuted }}>edited {fmtDateTime(c.updated_at)}{c.updated_by_name ? ` by ${c.updated_by_name}` : ''}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
